/**
 * Per-host sign-in branding for vertical preview domains.
 *
 * A tenant can reach the application through its own domain (pageonelife.com).
 * The bundle is the same, so the ONLY thing that changes is the look of the
 * anonymous sign-in screen: colors, fonts and wording taken from that domain's
 * own marketing site. Everything after authentication keeps using the tenant's
 * own `domain_branding` record — this registry never touches it.
 *
 * Tokens below mirror marketing/pageonelife/index.html so the login screen and
 * the landing it came from cannot drift apart.
 */

const PAGE_ONE_LIFE = {
  key: 'pageonelife',
  name: 'Page One Life',
  mark: 'P',
  tagline: 'Immigration practice software',
  welcome: 'Sign in to your firm workspace',
  homeUrl: 'https://pageonelife.com/',
  homeLabel: 'Back to pageonelife.com',
  // Self-serve registration and the field-service technician mode do not
  // belong to an immigration practice: access is granted by the firm.
  showRegister: false,
  showTechnicianToggle: false,
  // The bundle ships the FiveStarJob favicons in index.html. This brand has no
  // icon of its own yet, and the platform's "5" star in the tab would contradict
  // the domain the user came from, so the tab is left without an icon.
  showFavicon: false,
  fontImport:
    'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700' +
    '&family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap',
  colors: {
    // --v-theme-* equivalents used by the Vuetify components on the card.
    primary: '#1a3358',
    accent: '#2bb8a0',
    onAccent: '#0f1c2e',
    // Page / card chrome.
    background: '#faf7f1',
    surface: '#ffffff',
    ink: '#0f1c2e',
    muted: '#5c6b7a',
    line: '#d9d2c5',
  },
  fonts: {
    body: '"DM Sans", system-ui, sans-serif',
    display: 'Fraunces, Georgia, serif',
  },
}

const HOST_BRANDS = {
  'pageonelife.com': PAGE_ONE_LIFE,
  // `*.localhost` resolves to 127.0.0.1 in every current browser, so
  // http://pageonelife.localhost:5173/login renders the branded screen against
  // `npm run dev` with no /etc/hosts entry. Never reachable publicly.
  'pageonelife.localhost': PAGE_ONE_LIFE,
}

/** Marketing hosts often answer on both apex and www — accept either. */
function normalizeHost(host) {
  return String(host || '')
    .toLowerCase()
    .split(':')[0]
    .replace(/^www\./, '')
}

/**
 * Brand for the host the browser is currently on, or null for the default
 * FiveStarJob look (every host that is not explicitly registered above).
 */
export function resolveHostBrand(host) {
  const target = normalizeHost(
    host !== undefined ? host : (typeof window !== 'undefined' ? window.location.host : '')
  )
  if (!target) return null
  return HOST_BRANDS[target] || null
}

/**
 * CSS custom properties for the branded sign-in screen. Scoped to the login
 * page element on purpose: no global `documentElement` writes, so the theme of
 * the authenticated app is left untouched.
 */
export function hostBrandStyle(brand) {
  if (!brand) return null
  const c = brand.colors || {}
  const f = brand.fonts || {}
  return {
    '--v-theme-primary': hexToRgbTriplet(c.primary),
    '--v-theme-accent': hexToRgbTriplet(c.accent),
    '--v-theme-on-accent': hexToRgbTriplet(c.onAccent),
    '--brand-host-bg': c.background,
    '--brand-host-surface': c.surface,
    '--brand-host-ink': c.ink,
    '--brand-host-muted': c.muted,
    '--brand-host-line': c.line,
    '--brand-host-font': f.body,
    '--brand-host-display': f.display,
  }
}

/** Vuetify theme vars hold "r,g,b", not a hex string. */
function hexToRgbTriplet(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''))
  if (!m) return undefined
  const int = parseInt(m[1], 16)
  return `${(int >> 16) & 255},${(int >> 8) & 255},${int & 255}`
}

/**
 * Drop the platform favicons on a host whose brand has none of its own.
 * index.html hardcodes the FiveStarJob icon set (.ico, 16/32/192/512, apple
 * touch) plus the PWA manifest, all of which would put the platform's "5" star
 * in the tab of a domain that has nothing to do with it. Called once at boot so
 * the icon does not come back on later navigations; a tenant that later uploads
 * its own `favicon_url` still gets it applied by the branding store.
 */
export function applyHostBrandFavicon(brand) {
  if (typeof document === 'undefined') return
  const host = brand || resolveHostBrand()
  if (!host || host.showFavicon !== false) return
  document
    .querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"], link[rel="manifest"]')
    .forEach((el) => el.remove())
}

/** Load the brand's web fonts once (no-op when the tag is already present). */
export function ensureHostBrandFonts(brand) {
  if (!brand?.fontImport || typeof document === 'undefined') return
  const id = `host-brand-fonts-${brand.key}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = brand.fontImport
  document.head.appendChild(link)
}
